import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useApiSession } from '@/lib/useApiSession';
import { readinessRefetchMs, type ClosedWorldCheck } from '@/lib/analysisGate';

/** Mirror of `tres_core::preparedness::OntologyQualityMetrics`. Fractions are
 *  0.0..=1.0. */
export interface OntologyQualityMetrics {
  /** Composite readiness — equal-weight mean of validity, instantiation
   *  coverage, annotation coverage, and domain/range completeness. */
  overall: number;
  class_count: number;
  property_count: number;
  class_label_coverage: number;
  class_doc_coverage: number;
  property_label_coverage: number;
  property_doc_coverage: number;
  domain_range_completeness: number;
  orphan_property_count: number;
  class_instantiation_coverage: number;
  uninstantiated_class_count: number;
  isolated_class_count: number;
  /** Same-head-noun class pairs with no hierarchical relation — possible
   *  duplicates declared twice (review worklist; not yet scored). */
  redundancy_candidates: RedundancyCandidate[];
  connected_components: number;
  /** Fraction of DECLARED properties carried by at least one statement. */
  property_instantiation_coverage: number;
  /** Declared properties no statement uses — declared intent without data. */
  declared_unused_property_count: number;
  declared_unused_properties: PropertyFinding[];
  /** Predicates used in the data but never declared in the ontology. */
  undeclared_predicate_count: number;
  undeclared_predicates: PropertyFinding[];
  /** Declared classes with 2+ completeness signals missing, each absence
   *  paired with its grounding effect. */
  unfinished_classes: UnfinishedClass[];
  /** Closed-world validity verdict V(a) — present only on QROS (open-world RDF
   *  cannot falsify). Mirrors `tres_shared_substrate::ReadinessReport`. */
  closed_world?: ClosedWorldValidity | null;
  /** Where the whole-store closed-world check stands; null on RDF. While it
   *  runs or waits its turn behind another whole-store analysis,
   *  `closed_world` is null and the panel says what it is waiting for. */
  closed_world_check?: ClosedWorldCheck | null;
  /** VDB build state at analysis time. Schema writes rebuild the index
   *  asynchronously (tens of seconds); while `building`, these results were
   *  computed over the PREVIOUS index snapshot. */
  index: VdbIndexStatus;
  /** Pairs a reviewer dismissed as not redundant — suppressed from
   *  `redundancy_candidates`, restorable. */
  dismissed_redundancy: DismissedRedundancy[];
}

export interface RedundancyCandidate {
  a_uri: string;
  a_label: string;
  b_uri: string;
  b_label: string;
  shared_head: string;
}

export interface PropertyFinding {
  uri: string;
  label: string;
  usage_count: number;
}

export interface UnfinishedClass {
  uri: string;
  label: string;
  missing: { what: string; effect: string }[];
}

export interface DismissedRedundancy extends RedundancyCandidate {
  dismissed_by: string;
  dismissed_at: string;
}

/** Mirror of the `/api/v1/vdb/status` tagged union, embedded in the
 *  readiness response as `index`. */
export type VdbIndexStatus =
  | { state: 'pending' }
  | { state: 'building'; started_at: string }
  | { state: 'ready'; last_built: string; duration_ms: number }
  | { state: 'failed'; failed_at: string; error: string };

/** The closed-world verdict over the whole store: how much asserted data sits
 *  inside the ontology's valid region, and which rules push the rest out. */
export interface ClosedWorldValidity {
  statements_checked: number;
  valid: number;
  falsified: number;
  /** valid / statements_checked, 0.0..=1.0. */
  valid_fraction: number;
  /** Falsified-fact count per falsifying rule, highest first. */
  by_rule: { rule_id: string; count: number }[];
  /** A capped sample of concrete falsified facts. */
  samples: { rule_id: string; message: string; suggestion?: string | null }[];
}

/** Static ontology-readiness metrics for a graph. `graph` null/union → analyzed
 *  across all graphs (the backend normalizes the union pseudo-graph). */
export function useReadiness(graph: string | null) {
  const { token, ready } = useApiSession();

  return useQuery<OntologyQualityMetrics>({
    queryKey: ['ontology-readiness', graph ?? 'all', token ? 'auth' : 'noauth'],
    enabled: ready,
    staleTime: 30_000,
    // While the index rebuilds, or the closed-world check runs or waits its
    // turn, poll until the result lands, so the user never has to guess when
    // to hit Refresh.
    refetchInterval: (q) => readinessRefetchMs(q.state.data),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (graph) params.set('graph', graph);
      const qs = params.toString();
      const url = `/api/v1/vdb/ontology-readiness${qs ? `?${qs}` : ''}`;
      const r = await apiFetch<OntologyQualityMetrics>(url, token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}
